import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  platform_admin_required: '플랫폼 관리자만 계정 관리 기능을 사용할 수 있습니다.',
  platform_admin_check_failed: '플랫폼 관리자 권한을 확인하지 못했습니다.',
  search_query_invalid: '검색어는 2자 이상 입력해주세요.',
  target_user_required: '처리할 계정을 선택해주세요.',
  self_moderation_not_allowed: '현재 로그인한 본인 계정은 처리할 수 없습니다.',
  protected_platform_admin: '다른 플랫폼 관리자 계정은 처리할 수 없습니다.',
  moderation_reason_invalid: '처리 사유를 2자 이상 500자 이하로 입력해주세요.',
  confirmation_mismatch: '확인 문구가 일치하지 않습니다.',
  identity_type_invalid: '밴할 식별자 종류를 확인해주세요.',
  identity_value_invalid: '밴할 식별자 값을 확인해주세요.',
  ban_id_required: '해제할 밴 정보를 찾을 수 없습니다.',
  ban_not_found: '이미 해제됐거나 존재하지 않는 밴입니다.',
  platform_identity_banned: '플랫폼에서 차단된 식별자입니다.',
  moderation_failed: '계정 관리 요청을 처리하지 못했습니다.',
  scope_invalid: '갤러리(갤길) 또는 채널(챈길) 중에서 선택해주세요.',
  cannot_modify_full_admin: '제한 없는 플랫폼 관리자 계정은 이 화면에서 변경할 수 없습니다.',
  not_a_scoped_admin: '지정된 갤길·챈길 관리자가 아닙니다.',
};

function requireClient() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

function parsePayloadText(raw) {
  const text = String(raw || '').trim();
  if (!text) return { code: '', message: '' };
  try {
    const parsed = JSON.parse(text);
    return {
      code: String(parsed?.code || parsed?.error || '').trim(),
      message: String(parsed?.message || '').trim(),
    };
  } catch {
    return { code: '', message: text };
  }
}

async function readModerationErrorPayload(error) {
  const context = error?.context;
  if (context && typeof context.clone === 'function') {
    try {
      const payload = await context.clone().json();
      return {
        code: String(payload?.code || payload?.error || '').trim(),
        message: String(payload?.message || '').trim(),
      };
    } catch {
      try {
        return parsePayloadText(await context.clone().text());
      } catch {
        // Fall through to legacy error shapes.
      }
    }
  }
  if (typeof context?.body === 'string') return parsePayloadText(context.body);
  return { code: '', message: String(error?.message || error || '').trim() };
}

async function createModerationError(error, fallback) {
  const payload = await readModerationErrorPayload(error);
  const raw = `${payload.code} ${payload.message} ${String(error?.message || '')}`.trim();
  const matched = Object.keys(ERROR_MESSAGES).find((code) => payload.code === code || raw.includes(code));
  const next = new Error(ERROR_MESSAGES[matched] || payload.message || fallback || '계정 관리 요청을 처리하지 못했습니다.');
  next.code = matched || payload.code || error?.code || 'unknown_error';
  return next;
}

async function invoke(action, payload = {}, fallback) {
  const client = requireClient();
  const { data, error } = await client.functions.invoke('platform-account-moderation', {
    body: { action, ...payload },
  });
  if (error) throw await createModerationError(error, fallback);
  if (!data?.ok) throw await createModerationError({ message: data?.code }, fallback);
  return data;
}

export function searchPlatformAccounts(query) {
  return invoke('search', { query }, '계정 검색에 실패했습니다.');
}

export function loadPlatformModerationHistory() {
  return invoke('history', {}, '최근 계정 관리 이력을 불러오지 못했습니다.');
}

export function banPlatformAccount({ targetUserId, reason, confirmation }) {
  return invoke('ban_account', { targetUserId, reason, confirmation }, '계정 밴에 실패했습니다.');
}

export function unbanPlatformAccount({ targetUserId, reason }) {
  return invoke('unban_account', { targetUserId, reason }, '계정 밴 해제에 실패했습니다.');
}

export function deletePlatformAccount({ targetUserId, reason, confirmation, preserveBan = true }) {
  return invoke('delete_account', {
    targetUserId,
    reason,
    confirmation,
    preserveBan,
  }, '계정 삭제에 실패했습니다.');
}

export function banPlatformIdentity({ identityType, identityValue, reason }) {
  return invoke('ban_identity', { identityType, identityValue, reason }, '식별자 밴에 실패했습니다.');
}

export function unbanPlatformIdentity({ banId, reason }) {
  return invoke('unban_identity', { banId, reason }, '식별자 밴 해제에 실패했습니다.');
}

export function listScopedPlatformAdmins() {
  return invoke('list_scoped_admins', {}, '지정 관리자 목록을 불러오지 못했습니다.');
}

export function grantScopedPlatformAdmin({ targetUserId, scope }) {
  return invoke('grant_scoped_admin', { targetUserId, scope }, '관리자 지정에 실패했습니다.');
}

export function revokeScopedPlatformAdmin({ targetUserId }) {
  return invoke('revoke_scoped_admin', { targetUserId }, '관리자 지정 해제에 실패했습니다.');
}
